import { useCallback, useMemo, useState } from 'react';
import { interpolateI18n } from '../utils/interpolateI18n';
import {
  DEPENDENT_DEDUCTION_SECTION_ORDER,
  type DependentDeductionSectionId,
  firstIncompleteSection,
  initialSectionStatuses,
  nextSectionId,
  type SectionNavStatus,
  sectionLabelKey,
  sectionStatusesFromFlags,
  type TaxDeclarationSectionFlags,
} from './dependentDeductionSections';

export type DependentDeductionView = 'intro' | 'sectionMenu' | 'inSection';

export function useDependentDeductionFlow(t: (key: string) => string) {
  const [view, setView] = useState<DependentDeductionView>('intro');
  const [sectionStatuses, setSectionStatuses] = useState(initialSectionStatuses);
  const [activeSectionId, setActiveSectionId] = useState<DependentDeductionSectionId | null>(null);

  const completedCount = useMemo(
    () => DEPENDENT_DEDUCTION_SECTION_ORDER.filter((id) => sectionStatuses[id] === 'completed').length,
    [sectionStatuses]
  );

  const allowRowClick = completedCount > 0;
  const showEditHint = completedCount > 0 && view === 'sectionMenu';

  const applySectionFlags = useCallback((flags: TaxDeclarationSectionFlags) => {
    setSectionStatuses(sectionStatusesFromFlags(flags));
  }, []);

  const enterSection = useCallback((id: DependentDeductionSectionId) => {
    const status = sectionStatuses[id];
    if (status === 'locked') return;
    setActiveSectionId(id);
    setView('inSection');
  }, [sectionStatuses]);

  const returnToMenu = useCallback(() => {
    setActiveSectionId(null);
    setView('sectionMenu');
  }, []);

  const completeActiveSection = useCallback(() => {
    if (!activeSectionId) return;
    const completedId = activeSectionId;
    const nextId = nextSectionId(completedId);

    setSectionStatuses((prev) => {
      const next = { ...prev, [completedId]: 'completed' as SectionNavStatus };
      if (nextId) {
        next[nextId] = 'available';
      }
      return next;
    });
    setActiveSectionId(null);
    setView('sectionMenu');
  }, [activeSectionId]);

  const handleFooterPrimary = useCallback(() => {
    if (view === 'intro') {
      setView('sectionMenu');
      return;
    }
    if (view === 'sectionMenu') {
      const target = firstIncompleteSection(sectionStatuses);
      if (target) enterSection(target);
      return;
    }
    if (view === 'inSection') {
      completeActiveSection();
    }
  }, [view, sectionStatuses, enterSection, completeActiveSection]);

  const footerLabel = useMemo(() => {
    if (view === 'intro') return t('submitDocuments.dependentDeduction.start');
    if (view === 'inSection') {
      return t('submitDocuments.dependentDeduction.completeSection');
    }
    if (completedCount === 0) {
      return t('submitDocuments.dependentDeduction.start');
    }
    const next = firstIncompleteSection(sectionStatuses);
    if (!next) {
      return t('submitDocuments.chat.continue');
    }
    return t('submitDocuments.chat.continue');
  }, [view, completedCount, sectionStatuses, t]);

  const sectionIntroText = useMemo(() => {
    if (!activeSectionId) return '';
    const idx = DEPENDENT_DEDUCTION_SECTION_ORDER.indexOf(activeSectionId);
    const prefix = interpolateI18n(t('submitDocuments.dependentDeduction.sectionMenu.sectionPrefix'), {
      n: String(idx + 1),
    });
    return `${prefix} | ${t(sectionLabelKey(activeSectionId))}`;
  }, [activeSectionId, t]);

  return {
    view,
    setView,
    sectionStatuses,
    setSectionStatuses,
    activeSectionId,
    completedCount,
    allowRowClick,
    showEditHint,
    applySectionFlags,
    enterSection,
    returnToMenu,
    completeActiveSection,
    handleFooterPrimary,
    footerLabel,
    sectionIntroText,
    sectionLabelKey,
  };
}
